// ============================================================
// 店舗インスタンス設定(SSOT)
//
// 那覇(default) と 宮古(miyako) の「公開されてよい」店舗依存値を1箇所に集約する。
// 拠点依存値のハードコード散在を解消するための単一情報源。
//
// 正(constitution): booking-platform/md/stores.md。ここはその r-cal 実装。
// 値を変えるときは stores.md を先に更新し、本ファイルを追従させること。
//
// ⚠️ 秘匿値(Square アクセストークン、API パスワード、Pusher app *secret* 等)はここに置かない。
//    このファイルはビルドでバンドルに焼き込まれ、ブラウザから読めるため。
//    ここに置くのは URL・ブランド表記・機能フラグ・Square location ID・Pusher app key
//    といった「公開されてよい値」のみ。
//    ※ Pusher の app key はブラウザが購読に使う公開値なので、ここに置いてよい
//      (秘匿すべきは app secret で、そちらは Laravel の .env にありサーバ外に出ない)。
//      2026-07-28 以前は本コメントが app key を秘匿値として挙げており誤りだった。
// ============================================================

export type AppInstance = 'miyako' | 'default';

/**
 * 旧: 社内 API の共有キー。⚠️ 2026-10-09 廃止 — 値がこの公開バンドルに入っており、誰でも社内 API を呼べた。
 * 認証はスタッフのログイントークン（Bearer）に切り替えた（lib/internalApiAuth.ts が fetch で付与）。
 * 呼び出し箇所の `&api_key=` は「社内 API 呼び出し」の目印としてだけ残しており、値は空。
 * **ここに秘密を書き戻さないこと**（フロントの値は全て公開される）。
 */
export const LINE_SYNC_KEY = '';

/**
 * 店舗横断の共有テーブルを扱う「運用 API」のオリジン(那覇ホスト固定・全店舗が接続する)。
 *
 * `agent_requests` と `ai_action_logs` は**単一の DB にある共有テーブル**で、
 * 店舗の出し分けは URL ではなく `store` パラメータ('ncar'|'pit')で行う。
 * したがって宮古のブラウザもこのオリジンに接続するのが正しい。
 *
 * ⚠️ getLineApiUrl()(LINE チャット API)とは**性質が異なる**。あちらは
 *   line_users / line_messages という那覇 DB 固有のデータを読むため、
 *   宮古は接続してはいけない(接続すると那覇の顧客データが宮古に出る)。
 *   混同すると 2026-07-30 に修正した事故を再発させる。
 */
export const SHARED_OPS_API_ORIGIN = 'https://booking.trailernchu.co.jp';

/**
 * Square location token のグローバル登録簿(sqNum → location ID)。
 * 添字は .env.invoice の SQUARE_ACCESS_TOKEN_{n}/LOCATION_ID_{n} と同一体系。
 * 現在の店舗に依らず、既存 invoice リンク(sq:{n}:...)の解決に使う唯一の定義。
 *   '0','1' = 那覇 / '2' = 宮古(PIT)
 * ※ これは公開されてよい location ID。アクセストークン(秘匿)は含めない。
 */
export const SQUARE_LOCATION_TOKENS_BY_INDEX: Record<string, string> = {
  '0': 'LK10T0F948TWA', // 那覇
  '1': 'L26HXR0HJHTTQ', // 那覇1
  '2': 'LZPX9EZX1E8VN', // 宮古(PIT) — .env.invoice の LOCATION_ID_2(2026-07-23 設定済み)
};

/**
 * sqNum(Square 設定番号)から店舗キーを判定する。
 * サーバの store_resolver.php `resolve_store_from_square_index()` と同一定義。
 * 0,1 = 那覇 / 2 = 宮古(PIT)。ここを両者でずらすと店舗を跨いだ誤請求になる。
 */
export function storeKeyFromSquareIndex(sqNum: number): 'ncar' | 'pit' {
  return sqNum === 2 ? 'pit' : 'ncar';
}

export interface InstanceConfig {
  /** backend API ベースURL。null なら .env の REACT_APP_DISPATCH_API_URL を使う(那覇) */
  backendApi: string | null;
  /** 公開サイトのオリジン(顧客向けリンク・sys 系 PHP 呼び出しのベース) */
  siteOrigin: string;
  /** LINE 同期 API の URL。LINE 無効な店舗は null */
  lineApiUrl: string | null;
  /**
   * 自社チャット（new-n-car の LP チャット）の API。予約者チャット（session_key `res_…`）を
   * LINE ページに並べて読む・返信する（booking-platform/md/customer-chat.md Phase 2）。
   *
   * ⚠️ 店舗別 DB。那覇は n-car.jp、宮古は pit-rent.com を叩く。LINE API と違い宮古にも実在する。
   *    認証はスタッフの Bearer（この店舗の backend の Sanctum トークン）。LP 側が backend /auth/me で検証する。
   */
  chatApiUrl: string | null;
  /**
   * 自社チャット（LP）の Pusher アプリ（2026-10-11・リアルタイム化）。
   *
   * ⚠️ LP は那覇（n-car.jp）も宮古（pit-rent.com）も **同じ Pusher アプリ**（sys/.env の PUSHER_*）を使い、
   *    管理チャンネル `private-admin-chat` も共用している（店舗は payload の `store` で見分ける）。
   *    那覇の r-cal の Pusher アプリ（REACT_APP_PUSHER_APP_KEY）と同じアプリだが、宮古の r-cal は別アプリ
   *    （上の pusherKey）なので、自社チャットは **店舗に関係なく 2 本目の接続**で LP のアプリに繋ぐ。
   *    key は公開値（LP の `lib/pusher.js` と同じ）。購読認証は chat.php?action=pusher_auth（Bearer）。
   *    null は「リアルタイム無し＝ポーリングのみ」。
   */
  chatPusherKey: string | null;
  chatPusherCluster: string | null;
  /** 自社チャット API（chat.php）の店舗キー。Pusher payload の `store` と比較する */
  chatStoreKey: 'naha' | 'miyako';
  /**
   * Square 設定番号(sqNum)。.env.invoice の SQUARE_ACCESS_TOKEN_{n}/LOCATION_ID_{n} の添字で、
   * sys/ota/stores.php の `square_index` と一致させること(那覇=0 / 宮古=2)。
   * 請求書作成 API へ明示的に送る。送らないと 那覇(0)既定で発行されてしまう。
   */
  squareIndex: number;
  /**
   * Pusher の接続情報。**店舗ごとに別アプリ**を使う(同一アカウント内の別アプリ)。
   *
   * ⚠️ 那覇と宮古で同じアプリを使ってはいけない。backend 側の
   *    DispatchEvent::broadcastOn() が `dispatch-updates` をハードコードしており
   *    (別会社リポジトリ tokita-vworks/ncar_backend・両 backend で同一コード)、
   *    同一アプリだと両店舗が同じチャネルに publish して予約が混線する。
   *    line-chat チャネルも同様。アプリを分けることで名前空間ごと分離する。
   *
   * key は公開値(ブラウザが購読に使う)。null なら .env の REACT_APP_PUSHER_* を使う
   * (= 那覇。既存の挙動を変えないため)。
   * 空文字は「アプリ未作成」を意味し、isPusherEnabled() が false になりポーリングのまま。
   */
  pusherKey: string | null;
  pusherCluster: string | null;
  /**
   * 店舗の共用アカウントの login_id。無い店舗は null。
   *
   * カウンターの共用ログインで、誰が今対応しているかを「対応スタッフ」ピッカーで
   * 選び分ける運用に使う。個人アカウントでログインした場合は本人が対応者になるため
   * ピッカーは出さない(StaffSelector が login_id で判定する)。
   *
   * ⚠️ 2026-08-03 まで `user.username === 'staff'` と直書きしており、
   *   'staff' という login_id が**那覇にしか存在しない**ため、宮古では
   *   担当者ボタンが完全に不活性になっていた(メニュー内の「本日勤務」も開けず、
   *   出勤者を設定する導線が無かった＝スタッフ選択が永久に空)。
   */
  sharedAccountLogin: string | null;
  /** 機能フラグ(公開されてよい ON/OFF のみ) */
  features: {
    line: boolean;
    /**
     * 自社チャット（予約者チャット）を LINE ページ(/line)に並べる。両店舗 true。
     * LINE が無い宮古でも、このフラグだけで /line ページとメニューが出る（isChatBoardEnabled()）。
     */
    reservationChat: boolean;
    pusher: boolean;
    square: boolean;
    /**
     * 実況ウィンドウ(LiveWindow / Gemini 生成の注意喚起)。
     *
     * ⚠️ features.line とは別フラグ。実況の生成材料は
     *   A) agent_requests の保留中リクエスト  B) 直近3時間の LINE 受信
     *   C) ブラウザが送る本日の予約スナップショット
     * のうち **A と C が主材料**で、B が無くても成立する。
     * 宮古は LINE が無いので B が常に空になるだけで、到着連絡(A)の
     * 見過ごし防止という当面の任務は果たせる。
     * 詳細: md/refactoring/design-store-scoped-requests-2026-08.md §5
     */
    liveReport: boolean;
    /**
     * クルーズ船（/cruise）。クルーズ船の予約（宮古=平良港・那覇=那覇港）の日別料金と寄港予定の画面。
     * 両店舗 true（那覇は 2026-10-31 決定・実装 2）。正: booking-platform/md/pricing.md「クルーズ船の予約 — 2026-10-31 の決定」
     */
    cruisePricing: boolean;
  };
  /** メール署名・画面表示に使うブランド情報(4言語共通の値) */
  branding: {
    storeName: string;      // 和文店名
    storeNameEn: string;    // 欧文店名
    postalAddress: string;  // 〒＋和文住所
    addressEn: string;      // 欧文住所
    tel: string;            // 国内表記
    telIntl: string;        // 国際表記
    telHours: string;       // TEL 受付時間の表記(例 '9:00-21:00')
    lineUrl: string | null; // 緊急対応 LINE(無ければ null)
    website: string;        // 公開サイト URL
    operatorLine: string;   // メール末尾の運営会社行(法人名・URL)
  };
}

// 那覇(default)。全機能有効。値は既存実装・constitution と一致。
const NAHA: InstanceConfig = {
  backendApi: null, // .env の REACT_APP_DISPATCH_API_URL(= backend.n-car.info)
  siteOrigin: 'https://n-car.jp',
  lineApiUrl: 'https://booking.trailernchu.co.jp/sys/line/api_v2.php',
  chatApiUrl: 'https://n-car.jp/booking/api/chat.php',
  chatPusherKey: '1966347329d17ae2b01c', // LP の Pusher アプリ（公開 key・new-n-car lib/pusher.js と同じ）
  chatPusherCluster: 'ap3',
  chatStoreKey: 'naha',
  squareIndex: 0,
  pusherKey: null,      // .env の REACT_APP_PUSHER_APP_KEY を使う(既存挙動)
  pusherCluster: null,  // .env の REACT_APP_PUSHER_CLUSTER を使う
  sharedAccountLogin: 'staff',   // 那覇の共用アカウント(staffs id=17 / st_name も 'staff')
  features: { line: true, reservationChat: true, pusher: true, square: true, liveReport: true, cruisePricing: true },
  branding: {
    storeName: 'Nレンタカー 那覇空港店',
    storeNameEn: 'N RENT A CAR NAHA AIRPORT',
    postalAddress: '901-0145 沖縄県那覇市高良3丁目3-19',
    addressEn: '3-3-19 Takara, Naha-shi, Okinawa, Japan 901-0145',
    tel: '050-3363-7144',
    telIntl: '+81-50-3363-7144',
    telHours: '9:00-21:00',
    lineUrl: 'https://lin.ee/pkQzUiG',
    website: 'https://n-car.jp',
    operatorLine: 'Operated by 株式会社とれらんちゅ Trailernchu Inc. https://trailernchu.co.jp',
  },
};

// 宮古(miyako)。LINE/Pusher は当面 OFF(ポーリングで代替)。Square は 2026-07-27 に有効化
// (.env.invoice の SQUARE_ACCESS_TOKEN_2/LOCATION_ID_2 は 2026-07-23 設定済み、
//  sys/ota/stores.php も square_enabled=true・square_index=2 で稼働中)。
// 宮古の請求書は square_create_invoice.php の invoiceStoreConfig(2) により
// 2026-08-03 に即時公開へ変更（それまでは always_draft=true で必ず下書きだった）。
// ⚠️ 「LINE」は2種類あり別管理。混同しないこと(正: booking-platform/md/stores.md)。
//    - branding.lineUrl = 顧客向け LINE 公式アカウント → 宮古も開設済み(2026-07-27 確認)
//    - features.line    = r-cal の社内 LINE チャット連携 → 宮古は無効のまま
//    2026-07-27 以前は lineUrl を null にして「宮古は LINE 未開設」と誤記しており、
//    宮古のメールフッターから LINE 行が落ちていた。
const MIYAKO: InstanceConfig = {
  backendApi: 'https://backend-miyako.n-car.info/api',
  siteOrigin: 'https://pit-rent.com',
  lineApiUrl: null,
  chatApiUrl: 'https://pit-rent.com/booking/api/chat.php', // 宮古の LP チャット（別 DB）。LINE は無いが自社チャットはある
  chatPusherKey: '1966347329d17ae2b01c', // LP は両店舗で同じ Pusher アプリ（下の宮古専用アプリとは別）
  chatPusherCluster: 'ap3',
  chatStoreKey: 'miyako',
  squareIndex: 2,
  // 宮古専用の Pusher アプリ(2026-07-28 設定)。**那覇とは別アカウント**なので
  // 無料プランの上限もアカウント単位で分かれる。
  //   ⚠️ 那覇のキーを入れてはいけない(同一アプリだと backend の broadcastOn() が
  //      同じ 'dispatch-updates' に publish するため両店舗の予約が混線する)。
  //   app key は公開値。secret は backend-miyako/.env と sys/.env(MIYAKO_PUSHER_*)にあり、
  //   git には入らない。
  pusherKey: '4f8aea6df66feb8bed51',
  pusherCluster: 'ap3',
  // 宮古の共用アカウント(2026-08-03 に staffs id=7 として作成。那覇の id=17 と同じ login_id)。
  // ⚠️ ID は店舗ごとに独立採番なので**揃っていない**(那覇 id=17 / 宮古 id=7)。
  //    照合は login_id で行うこと。ID を揃えようとすると PAT(personal_access_tokens.
  //    tokenable_id)が宙に浮き、過去予約の担当者表示も入れ替わる。
  sharedAccountLogin: 'staff',
  features: { line: false, reservationChat: true, pusher: true, square: true, liveReport: true, cruisePricing: true },
  branding: {
    storeName: 'ピットレンタカー 宮古空港店',
    storeNameEn: 'PIT RENT A CAR MIYAKO AIRPORT',
    postalAddress: '906-0013 沖縄県宮古島市平良下里2511-7',
    addressEn: '2511-7 Shimozato, Hirara, Miyakojima-shi, Okinawa, Japan 906-0013',
    tel: '0980-79-7567',
    telIntl: '+81-980-79-7567',
    telHours: '9:00-20:00', // 宮古は 20:00 まで(那覇と異なる)
    lineUrl: 'https://lin.ee/UHTnUCT', // 顧客向け LINE(開設済み。社内連携は features.line=false)
    website: 'https://pit-rent.com',
    // ⚠️ 宮古の運営法人は n-car constitution(株式会社ピット)に準拠。r-cal からの
    //    宮古メール送信が本格運用される前に、正式な法人名・英名・URL を要確認。
    operatorLine: 'Operated by 株式会社ピット PIT Inc.',
  },
};

export const INSTANCES: Record<AppInstance, InstanceConfig> = {
  default: NAHA,
  miyako: MIYAKO,
};

/**
 * 現在の実行インスタンスを判定する。
 *   - 本番: window.location.hostname(pit-booking.* → miyako)
 *   - dev : REACT_APP_INSTANCE=miyako(npm run start:miyako)でも miyako 扱い
 */
export function getInstance(): AppInstance {
  if (process.env.REACT_APP_INSTANCE === 'miyako') return 'miyako';
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'pit-booking.trailernchu.co.jp' || host.startsWith('pit-booking.')) {
      return 'miyako';
    }
  }
  return 'default';
}

/** 現在のインスタンスの設定を返す。 */
export function getInstanceConfig(): InstanceConfig {
  return INSTANCES[getInstance()];
}
